/* eslint-disable react/jsx-no-duplicate-props */
import React, {
  ChangeEvent,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  ControllerFieldState,
  ControllerRenderProps,
} from 'react-hook-form/dist/types/controller';
import {
  InputAdornment,
  TextField,
} from '@mui/material';
import { FieldErrors } from 'react-hook-form/dist/types/errors';
import { getFinalErrorMessage } from 'lib/form/FormUtils';
import { UseFormTrigger } from 'react-hook-form';
import useAutoSave from 'lib/form/autosave';
import { UseFormStateReturn } from 'react-hook-form/dist/types';
import { Modification } from 'lib/Modification';
import FieldHistory from 'lib/form/FieldHistory';

type Props = {
  control: {
    field: ControllerRenderProps<any, any>,
    fieldState: ControllerFieldState,
    formState: UseFormStateReturn<any>,
  },
  placeholder?: string,
  label?: ReactNode,
  value?: string,
  type?: React.InputHTMLAttributes<unknown>['type'] | 'textarea',
  rows?: number,
  prefix?: ReactNode,
  suffix?: ReactNode,
  disabled?: boolean,
  requierd?: boolean,
  error?: FieldErrors,
  apiErrors?: { [key: string]: string[] }
  delayedAction?: (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void,
  autoSaveSubmit?: (value: string) => void,
  trigger?: UseFormTrigger<any>,
  submitting?: boolean,
  modifications?: { [field: string]: Modification[] }
  onChange?: (value: string) => void,
};

export default function TextFieldWrapper(
  {
    control,
    placeholder,
    label,
    type,
    prefix,
    suffix,
    rows,
    error,
    apiErrors,
    disabled,
    delayedAction,
    autoSaveSubmit,
    requierd,
    trigger,
    value,
    submitting,
    onChange,
    modifications,
  }: Props,
) {
  const { field } = control;
  const errorMessage = getFinalErrorMessage(field.name, error, apiErrors);
  const {
    hasSaveResult,
    getSaveResult,
    handleChange,
    onBlur,
  } = useAutoSave<string>(control, autoSaveSubmit, trigger, submitting);
  // eslint-disable-next-line no-undef
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
  const clearTimer = useCallback(() => {
    if (timer) {
      clearTimeout(timer);
    }
  }, [timer]);

  useEffect(() => clearTimer,
    [clearTimer]);

  const endTimerAction = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    clearTimer();
    if (delayedAction) {
      delayedAction(e);
    }
    onBlur(e.target.value);
  };

  const handleChangeValue = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    handleChange(e.target.value);
    if (delayedAction) {
      clearTimer();
      setTimer(setTimeout(() => {
        delayedAction(e);
      }, 1000));
    }
  };

  const fieldName = field.name.split('.').slice(-1)[0];
  const fieldModification = modifications && modifications[fieldName];
  return (
    <div
      className={`material-textfield-wrapper ${(
        fieldModification
        && fieldModification.length > 0
        && fieldModification
          .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0].updatedByAgent)
        ? 'danger-text' : ''}`}
    >
      <TextField
        {...field}
        onChange={(e) => {
          if (onChange) {
            onChange(e.target.value);
          }
          handleChangeValue(e);
        }}
        autoComplete="off"
        value={value || field.value || ''}
        error={!!errorMessage}
        helperText={errorMessage}
        fullWidth
        size="medium"
        disabled={disabled}
        type={(type === 'number' ? 'text' : type) || 'text'}
        inputProps={(type === 'number') ? { inputMode: 'numeric', pattern: '[0-9.]*' } : undefined}
        rows={rows}
        maxRows={!rows ? 10 : undefined}
        multiline={type === 'textarea'}
        placeholder={placeholder || undefined}
        label={label ? `${label}${requierd ? ' *' : ''}` : undefined}
        onBlur={endTimerAction}
        InputProps={{
          startAdornment: prefix && <InputAdornment position="start">{prefix}</InputAdornment>,
          endAdornment: (hasSaveResult || suffix || modifications)
            && (
              <InputAdornment position="end">{hasSaveResult ? getSaveResult() : suffix}{
                fieldModification
                && <FieldHistory history={fieldModification} />
              }
              </InputAdornment>
            ),
        }}
      />
    </div>
  );
}
